import { useState } from 'react';
import { Copy, ExternalLink, Check, Globe } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Compact strip that surfaces the public URL of an exhibition's landing
 * page (or a hash-anchored CTA on it) inside the admin. Copies + opens.
 *
 * Props:
 *   slug           — exhibition slug (required to render anything)
 *   hash           — optional hash suffix ("tickets" or "opencall")
 *   label          — small caption above the URL (defaults to "Public site")
 *   disabled       — greyed-out message shown when the flow is disabled
 */
export default function ExhibitionPublicUrlStrip({
  slug,
  hash,
  label = 'Public site',
  disabled,
  disabledReason,
  compact = false,
}) {
  const [copied, setCopied] = useState(false);
  // Public share links must always point at the LIVE site, never the
  // preview/admin origin. Configurable via REACT_APP_PUBLIC_SITE_URL.
  const publicBase = (process.env.REACT_APP_PUBLIC_SITE_URL || window.location.origin).replace(/\/$/, '');
  const url = slug ? `${publicBase}/exhibitions/${slug}${hash ? `#${hash}` : ''}` : null;

  if (disabled) {
    return (
      <div
        className={`border border-[#1a1a1a] bg-[#0a0a0a] p-3 ${compact ? '' : 'mb-4'}`}
        data-testid="exhibition-public-url-strip-disabled"
      >
        <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#555] mb-1">
          {label}
        </div>
        <p className="text-[11px] font-mono text-[#666]">
          {disabledReason || 'Enable the flow on the exhibition to expose a public URL here.'}
        </p>
      </div>
    );
  }

  if (!url) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('URL copied');
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error('Copy failed — long-press to copy manually');
    }
  };

  return (
    <div
      className={`border border-[#1a1a1a] bg-[#0a0a0a] p-3 ${compact ? '' : 'mb-4'}`}
      data-testid="exhibition-public-url-strip"
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <Globe className="w-3 h-3 text-[#90E0EF]/70" />
        <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#666]">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <div
          className="flex-1 text-[11px] font-mono text-[#90E0EF] px-2 py-1.5 bg-[#050505] border border-[#1a1a1a] truncate select-all"
          data-testid="exhibition-public-url-value"
        >
          {url}
        </div>
        <button
          type="button"
          onClick={copy}
          data-testid="exhibition-public-url-copy"
          className="p-1.5 border border-[#1a1a1a] hover:border-[#90E0EF]/40 text-[#888] hover:text-[#90E0EF] transition-colors"
          title="Copy URL"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          data-testid="exhibition-public-url-open"
          className="p-1.5 border border-[#1a1a1a] hover:border-[#90E0EF]/40 text-[#888] hover:text-[#90E0EF] transition-colors"
          title="Open public page"
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
}
